import { useState, useEffect, useCallback } from 'react';
import apiClient from '../lib/api/apiClient';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserContext } from '../contexts/UserContext';

interface Player {
  id: number;
  first_name: string;
  last_name: string;
  position_1: string;
  position_2: string;
  position_3: string;
  mlb_team: string;
  image?: string;
  bats?: string;
  throws?: string;
  rule_five_eligibility?: string;
}

interface Contract {
  year_signed: number | null;
  current_salary: number;
  salaries: Array<{ year: number; salary: number }>;
  contract_types: Array<{ year: number; type: string }>;
  buyouts: Array<{ year: number; amount: number }>;
  total_value?: number; // Added for total contract value
}

interface RosterPlayer {
  id: number;
  rwPlayer: Player;
  level: string;
  status: string;
  status_until: string | null;
  playerContract: Contract;
  is_40_man: boolean;
  year: number;
  trade_status?: string | null;
  trade_status_until?: string | null;
}

interface ResearchFilters {
  name?: string;
  positions?: string[];
  levels?: string[];
  teams?: string[];
  page?: number;
  rowsPerPage?: number;
}

interface ResearchPlayersResponse {
  players: RosterPlayer[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
}

interface UseTeamRosterReturn {
  players: RosterPlayer[];
  total_payroll: number;
  activeRosterSize: number | null;
  fortyManRosterSize: number | null;
  hasFranchiseTag: number | false | null;
  hasQualifyingOffer: boolean | null;
  rosterLocked: boolean;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  searchForPlayers: (leagueId: number, filters?: ResearchFilters) => Promise<ResearchPlayersResponse>;
  rosterMove: (playerId: number, transactionName: string) => Promise<unknown>;
}

export const useTeamRoster = (teamId: number): UseTeamRosterReturn => {
  const [players, setPlayers] = useState<RosterPlayer[]>([]);
  const [total_payroll, setTotalPayroll] = useState<number>(0);
  const [activeRosterSize, setActiveRosterSize] = useState<number | null>(null);
  const [fortyManRosterSize, setFortyManRosterSize] = useState<number | null>(null);
  const [hasFranchiseTag, setHasFranchiseTag] = useState<number | false | null>(null);
  const [hasQualifyingOffer, setHasQualifyingOffer] = useState<boolean | null>(null);
  const [rosterLocked, setRosterLocked] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { myTeam } = useUserContext();

  const fetchRoster = useCallback(async () => {
    console.log('🔍 useTeamRoster fetchRoster called with teamId:', teamId);
    try {
      setLoading(true);
      // (currentYear was unused)
      const response = await apiClient.get(`/roster/full-roster/${teamId}`);
      console.log('✅ useTeamRoster API response received for teamId:', teamId, response.data);
      setPlayers(response.data.players);
      setTotalPayroll(response.data.total_payroll);
      setActiveRosterSize(response.data.activeRosterSize ?? null);
      setFortyManRosterSize(response.data.fortyManRosterSize ?? null);
      // hasFranchiseTag is either false or the player ID (number)
      setHasFranchiseTag(response.data.hasFranchiseTag !== undefined ? response.data.hasFranchiseTag : null);
      setHasQualifyingOffer(response.data.hasQualifyingOffer ?? null);
      setRosterLocked(response.data.locked ?? false);
      setError(null);
    } catch (err) {
      console.error('❌ useTeamRoster API error for teamId:', teamId, err);
      setError(err instanceof Error ? err : new Error('Failed to fetch roster'));
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const searchForPlayers = async (leagueId: number, filters: ResearchFilters = {}): Promise<ResearchPlayersResponse> => {
    try {
      const params: Record<string, unknown> = {
        ...filters,
        page: filters.page || 0,
        rowsPerPage: filters.rowsPerPage || 300
      };

      // Convert arrays to comma-separated strings for API
      if (params.positions && Array.isArray(params.positions)) {
        params.positions = (params.positions as string[]).join(',');
      }
      if (params.levels && Array.isArray(params.levels)) {
        params.levels = (params.levels as string[]).join(',');
      }
      if (params.teams && Array.isArray(params.teams)) {
        params.teams = (params.teams as string[]).join(',');
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await apiClient.get(`/roster/research/${leagueId}`, { params });
      return response.data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to search players'));
      throw err;
    }
  };

  const rosterMove = async (playerId: number, transactionName: string): Promise<unknown> => {
    try {
      // Get leagueId from store
      const myLeague = useMyLeagueStore.getState().myLeague;

      if (!myLeague?.id) {
        throw new Error('No league selected');
      }

      if (!myTeam) {
        throw new Error('No team selected');
      }

      const response = await apiClient.post('/roster/roster-move', {
        leagueId: myLeague.id,
        teamId: myTeam,
        playerId,
        transactionName
      });

      // Refresh the roster after a successful move
      await fetchRoster();

      return response.data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to make roster move'));
      throw err;
    }
  };

  useEffect(() => {
    console.log('🔄 useTeamRoster useEffect triggered with teamId:', teamId);
    if (teamId && teamId !== 1) {
      fetchRoster();
    }
  }, [teamId, fetchRoster]);

  return {
    players,
    total_payroll,
    activeRosterSize,
    fortyManRosterSize,
    hasFranchiseTag,
    hasQualifyingOffer,
    rosterLocked,
    loading,
    error,
    refetch: fetchRoster,
    searchForPlayers,
    rosterMove
  };
}; 